import { useCallback, useEffect } from 'react';
import { useProjectModulesQuery } from '~/hooks/use-project-modules';
import { useTwxEnabled } from '~/hooks/use-twx-enabled';
import { useAppUser } from '~/components/molecules/appuser/appuser-context';
import { get, find } from 'lodash';
import { Module } from '@bechtel/types';

export interface ModulePermission {
  canViewPlanningModule: boolean;
  canViewTimeSheetModule: boolean;
  canViewCrewChangeModule: boolean;
  moduleLoading: boolean;
  canViewBlockModule: boolean;
  canViewWorkPackageModule: boolean;
  canViewRodRoomModule: boolean;
  canViewReportsModule: boolean;
}

export function useModulePermission(): ModulePermission {
  const { user } = useAppUser();
  const { twxEnabled } = useTwxEnabled();
  const [getModules, { data: moduleData, loading: moduleLoading }] = useProjectModulesQuery();

  useEffect(() => {
    if (!user || !user.project || !user.project.id) return;
    getModules({ variables: { id: user?.project?.id } });
  }, [getModules, user]);

  const moduleList = get(moduleData, 'projectConfigById.modules', []);

  const canViewModule = useCallback(
    (moduleType: Module) => {
      if (moduleList.length == 0) return false;
      const moduleItem = find(moduleList, (item) => {
        return item.moduleTypeId === moduleType;
      });
      return moduleItem && moduleItem.isActive ? moduleItem.isActive : false;
    },
    [moduleList]
  );

  const canViewPlanningModule = canViewModule(Module.PLANNING);
  const canViewTimeSheetModule = canViewModule(Module.TIMESHEET);
  const canViewCrewChangeModule = canViewModule(Module.CREW_CHANGE);
  const canViewBlockModule = canViewModule(Module.BLOCKS);
  const canViewWorkPackageModule = canViewModule(Module.WORK_PACKAGES) && !twxEnabled;
  const canViewRodRoomModule = canViewModule(Module.ROD_ROOM);
  const canViewReportsModule = canViewModule(Module.REPORTS);

  return {
    canViewPlanningModule,
    canViewTimeSheetModule,
    canViewCrewChangeModule,
    moduleLoading,
    canViewBlockModule,
    canViewWorkPackageModule,
    canViewRodRoomModule,
    canViewReportsModule,
  };
}
